import type { ReactNode } from "react";
import { Link, useLocation } from "react-router";
import {
  AppBar,
  BottomNavigation,
  BottomNavigationAction,
  Box,
  IconButton,
  Paper,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useColorScheme,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Icon, Wordmark } from "@despejo/ui";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "../lib/supabase.js";

const ALL_NAV: { to: string; label: string; icon: import("@despejo/ui").IconName; adminOnly?: boolean }[] = [
  { to: "/", label: "Inicio", icon: "home" },
  { to: "/contribuyentes", label: "Contribuyentes", icon: "badge" },
  { to: "/calendario", label: "Calendario", icon: "calendar_month" },
  { to: "/orientacion", label: "Orientación", icon: "help" },
  { to: "/legal", label: "Normativa", icon: "description", adminOnly: true },
  { to: "/ajustes", label: "Ajustes", icon: "settings" },
];

function active(pathname: string, to: string) {
  return to === "/" ? pathname === "/" : pathname.startsWith(to);
}

/** M3 navigation rail (medium/expanded) — navigation bar on compact screens. */
export function AppShell({ children }: { children: ReactNode }) {
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down("sm"));
  const { pathname } = useLocation();
  const { mode, setMode } = useColorScheme();
  const nextMode = mode === "dark" ? "light" : mode === "light" ? "system" : "dark";
  // Global views are for platform operators only (private.platform_admins), not every organization owner.
  const admin = useQuery({ queryKey: ["is-platform-admin"], queryFn: async () => (await supabase.rpc("is_platform_admin")).data === true });
  const NAV = ALL_NAV.filter((n) => !n.adminOnly || admin.data === true);

  return (
    <Box sx={{ minHeight: "100dvh", bgcolor: "background.default", display: "flex" }}>
      {!compact && (
        <Box
          component="nav"
          aria-label="Principal"
          sx={{ width: 88, flexShrink: 0, position: "sticky", top: 0, height: "100dvh", borderRight: 1, borderColor: "divider", pt: 2 }}
        >
          <Stack spacing={1.5} sx={{ alignItems: "center" }}>
            <Box component={Link} to="/" aria-label="Despejo — inicio" sx={{ color: "text.primary", textDecoration: "none", mb: 2 }}>
              <Box component="img" src="/icon.svg" alt="" sx={{ width: 40, height: 40, filter: mode === "dark" ? "invert(1)" : "none" }} />
            </Box>
            {NAV.map((n) => {
              const on = active(pathname, n.to);
              return (
                <Stack key={n.to} component={Link} to={n.to} spacing={0.5} sx={{ alignItems: "center", textDecoration: "none", color: on ? "text.primary" : "text.secondary", width: 80 }}>
                  <Box sx={{ width: 56, height: 32, borderRadius: 16, display: "grid", placeItems: "center", bgcolor: on ? "m3.secondaryContainer" : "transparent" }}>
                    <Icon name={n.icon} fill={on} />
                  </Box>
                  <Typography variant="caption" sx={{ fontWeight: on ? 700 : 500, fontSize: 11, letterSpacing: 0, maxWidth: 84, textAlign: "center", lineHeight: 1.3 }}>
                    {n.label}
                  </Typography>
                </Stack>
              );
            })}
          </Stack>
        </Box>
      )}
      <Box sx={{ flex: 1, minWidth: 0, pb: compact ? 10 : 0 }}>
        <AppBar position="sticky">
          <Toolbar sx={{ gap: 1 }}>
            {compact && <Wordmark size={18} />}
            <Box sx={{ flex: 1 }} />
            <Tooltip title={`Tema: ${mode === "system" ? "del sistema" : mode === "dark" ? "oscuro" : "claro"}`}>
              <IconButton onClick={() => setMode(nextMode)} aria-label="Cambiar tema">
                <Icon name={mode === "dark" ? "dark_mode" : mode === "light" ? "light_mode" : "contrast"} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Salir">
              <IconButton onClick={() => supabase.auth.signOut()} aria-label="Salir">
                <Icon name="logout" />
              </IconButton>
            </Tooltip>
          </Toolbar>
        </AppBar>
        <Box component="main" sx={{ px: { xs: 2, sm: 3, md: 4 }, py: 3, maxWidth: 1240, mx: "auto" }}>
          {children}
        </Box>
      </Box>
      {compact && (
        <Paper component="nav" aria-label="Principal" square sx={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 10, borderTop: 1, borderColor: "divider" }}>
          <BottomNavigation showLabels value={NAV.findIndex((n) => active(pathname, n.to))} sx={{ height: 72, bgcolor: "m3.surfaceContainer" }}>
            {NAV.map((n) => (
              <BottomNavigationAction
                key={n.to}
                component={Link}
                to={n.to}
                label={n.label}
                icon={<Icon name={n.icon} fill={active(pathname, n.to)} />}
                sx={{
                  minWidth: 0,
                  px: 0.25,
                  // MUI enlarges the selected label; with five or six items "Contribuyentes" then ran into its neighbour.
                  "& .MuiBottomNavigationAction-label, &.Mui-selected .MuiBottomNavigationAction-label": { fontSize: 11, letterSpacing: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" },
                }}
              />
            ))}
          </BottomNavigation>
        </Paper>
      )}
    </Box>
  );
}
